import {
  ArrowRight, BadgeDollarSign, Check, ExternalLink, Mail, ShieldCheck,
} from 'lucide-react'
import {
  Link,
} from 'react-router-dom'
import {
  OnboardingEvents,
  trackEvent,
} from '../analytics/posthog'
import {
  REFER_FROM_AMBASSADORS_PATH,
} from '../utils/referral'

const OPT_IN_EMAIL = 'support@catchagent.ai'
const OPT_IN_HREF = `mailto:${OPT_IN_EMAIL}?subject=${encodeURIComponent(
  'Catch Ambassador Program opt-in',
)}&body=${encodeURIComponent(
  "Hi Catch team,\n\nI'd like to opt in to the $300 Ambassador Program.\n\nMy Catch account email is: \n",
)}`

const steps = [
  {
    number: '01',
    title: 'Opt in with us',
    body: 'Email the Catch team and wait for written confirmation that you are enrolled before you start referring.',
  },
  {
    number: '02',
    title: 'Share your link',
    body: 'Open the Refer page and send your personal Catch referral link to people who would genuinely benefit from Catch.',
  },
  {
    number: '03',
    title: 'They become a customer',
    body: 'Your referral completes the trial and makes their first successful monthly payment.',
  },
  {
    number: '04',
    title: 'You earn $300 cash',
    body: 'When their second monthly payment succeeds, that referral qualifies and you earn $300 in cash.',
  },
]

const qualifications = [
  'You must be approved and enrolled by Catch before the referral is made.',
  'The new customer must sign up through your personal link from the Refer page.',
  'Their trial, first monthly payment, and second monthly payment must all be completed successfully.',
  'When Catch verifies the payout, we settle the $300 cash reward in place of the standard referral credit for that referral. It is not an additional reward.',
]

const qualificationTimeline = [
  { label: 'Trial', detail: 'Completed', isPayout: false },
  { label: 'Month 1', detail: 'First payment', isPayout: false },
  { label: 'Month 2', detail: 'Second payment · $300 earned', isPayout: true },
]

export function AmbassadorsPage(): JSX.Element {
  return (
    <div className="mx-auto max-w-6xl pb-8">
      <section className="relative overflow-hidden rounded-[28px] bg-catch-black text-catch-white">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-28 -top-36 h-[32rem] w-[32rem] rounded-full bg-catch-red/40 blur-[110px]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-40 left-1/3 h-80 w-80 rounded-full bg-amber-200/10 blur-[100px]"
        />

        <div className="relative grid gap-12 px-6 py-10 sm:px-10 sm:py-14 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:px-14 lg:py-20">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-catch-white/15 bg-catch-white/10 px-3 py-1.5 text-xs font-medium uppercase tracking-[0.16em] text-catch-white/70">
              <span className="h-1.5 w-1.5 rounded-full bg-catch-red" />
              Private ambassador offer · Opt in
            </div>

            <h1 className="mt-7 max-w-3xl font-display text-[clamp(3.25rem,8vw,6.75rem)] uppercase leading-[0.82] tracking-[-0.025em]">
              Your intro.<br />Your $300.
            </h1>
            <p className="mt-7 max-w-xl font-serif text-[clamp(1.5rem,3vw,2.15rem)] italic leading-tight text-catch-red">
              Earn cash for every Catch customer who stays for month two.
            </p>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-catch-white/65 sm:text-lg">
              You already know who needs less admin in their life. Introduce them
              with your personal Catch link. Once they make their second monthly
              payment, you earn <span className="font-medium text-catch-white">$300 cash</span>.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <a
                href={OPT_IN_HREF}
                onClick={() => trackEvent(OnboardingEvents.AMBASSADOR_OFFER_OPT_IN_CLICKED, {
                  surface: 'hero',
                })}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-catch-white px-5 py-3.5 text-sm font-medium text-catch-black shadow-soft transition-[background-color,transform] hover:bg-catch-beige active:scale-[0.98]"
              >
                <Mail className="h-4 w-4" />
                Ask to join
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link
                to={REFER_FROM_AMBASSADORS_PATH}
                onClick={() => trackEvent(OnboardingEvents.AMBASSADOR_OFFER_REFER_CLICKED, {
                  surface: 'hero',
                })}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-catch-white/15 bg-catch-white/10 px-5 py-3.5 text-sm font-medium text-catch-white transition-[background-color,transform] hover:bg-catch-white/15 active:scale-[0.98]"
              >
                Open your Refer page
                <ExternalLink className="h-4 w-4" />
              </Link>
            </div>

            <p className="mt-4 max-w-xl text-xs leading-relaxed text-catch-white/45">
              Enrollment is not automatic. Your participation begins only after
              Catch confirms your approval in writing.
            </p>
          </div>

          <div className="mx-auto w-full max-w-md lg:ml-auto">
            <div className="overflow-hidden rounded-[24px] border border-catch-white/15 bg-catch-white text-catch-black shadow-2xl shadow-black/20">
              <div className="flex items-center justify-between border-b border-catch-beige-dark px-6 py-5">
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-catch-black/40">
                    Ambassador payout
                  </p>
                  <p className="mt-1 text-sm font-medium">Per qualifying referral</p>
                </div>
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-catch-red/10 text-catch-red">
                  <BadgeDollarSign className="h-5 w-5" />
                </span>
              </div>

              <div className="px-6 py-8 sm:px-8">
                <p className="font-display text-[5.5rem] leading-[0.8] tracking-[-0.04em]">$300</p>
                <div className="mt-3 flex items-center gap-2">
                  <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-700">
                    Cash
                  </span>
                  <span className="text-xs text-catch-black/45">not hotel credit</span>
                </div>

                <div className="mt-8 border-t border-catch-beige-dark pt-6">
                  <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-catch-black/40">
                    The qualifying moment
                  </p>
                  <div className="mt-5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0">
                    {qualificationTimeline.map((step) => (
                      <div className="contents" key={step.label}>
                        <div className="flex flex-col items-center">
                          <span className={`flex h-7 w-7 items-center justify-center rounded-full ${
                            step.isPayout
                              ? 'bg-catch-red text-white'
                              : 'bg-catch-beige text-catch-black/60'
                          }`}>
                            <Check className="h-3.5 w-3.5" />
                          </span>
                          {!step.isPayout && <span className="h-8 w-px bg-catch-beige-dark" />}
                        </div>
                        <div className="pb-5 pt-1">
                          <p className="text-sm font-medium leading-none">{step.label}</p>
                          <p className={`mt-1 text-xs ${step.isPayout ? 'font-medium text-catch-red' : 'text-catch-black/45'}`}>
                            {step.detail}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="border-t border-catch-beige-dark bg-catch-beige px-6 py-4 text-xs leading-relaxed text-catch-black/55 sm:px-8">
                Catch settles this cash offer in place of the standard referral
                credit for the same referral; it is not an additional reward.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-1 py-16 sm:px-4 sm:py-20">
        <div className="max-w-2xl">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-catch-red">How it works</p>
          <h2 className="mt-3 font-display text-4xl uppercase leading-[0.95] text-catch-black sm:text-5xl">
            Four steps.<br />One very simple payout.
          </h2>
        </div>

        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {steps.map((step) => (
            <article
              key={step.number}
              className="flex min-h-[230px] flex-col rounded-2xl border border-catch-beige-dark bg-white p-6 shadow-soft"
            >
              <span className="font-display text-2xl text-catch-red">{step.number}</span>
              <h3 className="mt-auto pt-8 font-display text-xl uppercase leading-tight text-catch-black">
                {step.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-catch-black/55">{step.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="grid overflow-hidden rounded-[28px] border border-catch-beige-dark bg-catch-beige lg:grid-cols-[0.85fr_1.15fr]">
        <div className="flex flex-col justify-between bg-catch-red p-7 text-white sm:p-10 lg:p-12">
          <div>
            <ShieldCheck className="h-8 w-8" />
            <p className="mt-8 text-xs font-medium uppercase tracking-[0.18em] text-white/65">
              The important part
            </p>
            <h2 className="mt-3 font-display text-4xl uppercase leading-[0.92] sm:text-5xl">
              Opt in<br />before you refer.
            </h2>
          </div>
          <p className="mt-10 max-w-sm text-sm leading-relaxed text-white/75">
            This is a private, approval-only offer. Sending a referral link by
            itself does not enroll you in the $300 program.
          </p>
        </div>

        <div className="p-7 sm:p-10 lg:p-12">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-catch-black/40">
            What qualifies
          </p>
          <ul className="mt-6 space-y-5">
            {qualifications.map((qualification) => (
              <li key={qualification} className="flex items-start gap-3 text-sm leading-relaxed text-catch-black/70 sm:text-base">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-catch-red">
                  <Check className="h-3.5 w-3.5" />
                </span>
                {qualification}
              </li>
            ))}
          </ul>
          <p className="mt-8 border-t border-catch-beige-dark pt-6 text-xs leading-relaxed text-catch-black/45">
            Referrals and payments are subject to verification. Existing users,
            self-referrals, duplicate accounts, cancelled or refunded payments,
            and fraudulent activity do not qualify. Catch may change or end this
            limited promotion at any time.
          </p>
        </div>
      </section>

      <section className="px-4 py-16 text-center sm:py-20">
        <p className="font-serif text-2xl italic text-catch-red sm:text-3xl">
          Know the right people for Catch?
        </p>
        <h2 className="mx-auto mt-3 max-w-2xl font-display text-4xl uppercase leading-[0.92] text-catch-black sm:text-6xl">
          Let&apos;s make it official.
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-catch-black/55 sm:text-base">
          Email us to request access. We&apos;ll confirm whether you&apos;re enrolled,
          then you can start sharing your personal link.
        </p>
        <a
          href={OPT_IN_HREF}
          onClick={() => trackEvent(OnboardingEvents.AMBASSADOR_OFFER_OPT_IN_CLICKED, {
            surface: 'footer',
          })}
          className="mt-8 inline-flex items-center justify-center gap-2 rounded-xl bg-catch-black px-6 py-4 text-sm font-medium text-white shadow-soft transition-[background-color,transform] hover:bg-catch-black/90 active:scale-[0.98]"
        >
          <Mail className="h-4 w-4" />
          Apply via {OPT_IN_EMAIL}
          <ArrowRight className="h-4 w-4" />
        </a>
      </section>
    </div>
  )
}
